<template>
  <div class="activity-stage">
    <div class="not-the-same-wrapper">
      <div class="steps-container">
        <div class="step step-0">
          <img class="substep" id="image-0" :src="imageLeftSrc" alt="" />
          <div
            :key="step.stepIndex"
            v-for="step in stepsLeft"
            v-show="step.stepIndex <= progress.stepIndex"
          >
            <blinking-cursor-text
              :id="`step-${step.stepIndex}`"
              :key="step.stepIndex"
              :text="step.answer"
              :text-step="step.stepIndex"
              :round-id="currentRound.id"
              :class-name="step.isTitle ? 'type-line title' : 'type-line substep'"
            >
            </blinking-cursor-text>
          </div>
        </div>

        <div class="step step-1">
          <img class="substep" id="image-1" :src="imageRightSrc" alt="" />
          <div
            :key="step.stepIndex"
            v-for="step in stepsRight"
            v-show="step.stepIndex <= progress.stepIndex"
          >
            <blinking-cursor-text
              :id="`step-${step.stepIndex}`"
              :key="step.stepIndex"
              :text="step.answer"
              :text-step="step.stepIndex"
              :round-id="currentRound.id"
              :class-name="step.isTitle ? 'type-line title' : 'type-line substep'"
            >
            </blinking-cursor-text>
          </div>
        </div>
      </div>
    </div>

    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper],

  components: {
    BlinkingCursorText
  },

  data() {
    return {};
  },
  computed: {
    steps() {
      if (!this.currentRound || !this.currentRound.steps) {
        return [];
      }
      return this.currentRound.steps;
    },
    stepsLeft() {
      if (!this.currentRound || !this.currentRound.steps) {
        return [];
      }
      return this.currentRound.steps.filter(({ isLeft }) => isLeft);
    },
    stepsRight() {
      if (!this.currentRound || !this.currentRound.steps) {
        return [];
      }
      return this.currentRound.steps.filter(({ isLeft }) => !isLeft);
    },
    imageLeftSrc() {
      const [firstLeftStep] = this.stepsLeft;
      return firstLeftStep ? firstLeftStep.image : null;
    },
    imageRightSrc() {
      const [firstRightStep] = this.stepsRight;
      return firstRightStep ? firstRightStep.image : null;
    }
  },
  methods: {
    async loadActivity() {
      const activityState = this.getActivityState();
      this.createRounds(activityState);
      await this.loadImages(activityState);
    },

    setContainerHeight() {
      const keyboard = document.querySelector('.phoenix-keyboard-wrapper');
      const container = document.querySelector('.not-the-same-wrapper');
      container.style.height = `calc(100% - ${keyboard.offsetHeight + 45}px)`;
    },

    async loadImages({ rounds }) {
      rounds.forEach(({ steps }) => {
        steps.forEach(({ image }) => {
          this.queueImage(image);
        });
      });

      this.downloadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;
      return {
        rounds,
        instructions: {
          delay: 4,
          voice: `${this.voiceInstructionsPrefix}/notthesame-${this.getRandomVoice()}-1.mp3`,
          written: 'Type each word and description.'
        }
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();
      this.$nextTick(() => {
        this.setContainerHeight();
        TweenMax.to(['#step-0', '#image-0'], 1, { opacity: 1 });
      });
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.completeStep();
        this.setCurrentStep();

        const { stepIndex } = this.progress;
        const stepsTotal = this.currentRound.steps.length;

        if (this.isEndOfActivity && stepIndex === stepsTotal) {
          // end of activity
          this.playCongrats();
          // play congrats first, then end the activity after delay
          TweenMax.delayedCall(1.5, () => this.completeActivity(false));
          return;
        }

        if (stepIndex === 2) {
          // show image
          TweenMax.to('#image-1', 1, { opacity: 1 });
        }

        // show paragraph to be typed
        TweenMax.to(`#step-${stepIndex}`, 1, { opacity: 1 });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.substep,
.type-line {
  opacity: 0;
}

.not-the-same-wrapper {
  margin-top: 50px;
  padding-left: 50px;
  padding-right: 50px;

  .steps-container {
    height: 100%;

    .step {
      display: inline-block;
      height: 100%;
      width: 40%;

      img {
        height: 30%;
        max-height: 250px;
      }

      &:last-child {
        float: right;
      }

      .type-line {
        font-size: 20px;

        &.title {
          margin-bottom: 5px;
        }
      }
    }
  }
}
</style>
